import React, { useEffect, useState } from 'react';
import * as S from './Table.styled';
import { Avatar, Flex, Image, Popover, Space, Table as TableAnt, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import { LineChartOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
	IAvatar,
	IConsultation,
	ICountry,
	IFavourite,
	ILog,
	IRole,
	IUser,
	IUserType,
} from 'interfaces/main';
import type { FilterValue, SortOrder, SorterResult } from 'antd/es/table/interface';
import { useAppSelector } from 'hooks/useStore';
import { LibrariesSelector } from 'features/Libraries/LibrariesSlice';
import { sortBy, uniq } from 'lodash';
import Logs from 'components/Logs';
import Badge from 'antd/lib/badge';

const { Text } = Typography;

interface TableParams {
	pagination?: TablePaginationConfig;
	sortField?: string;
	sortOrder?: SortOrder;
	filters?: Record<string, FilterValue>;
}

export interface Props {
	items: IUser[];
}

const Table: React.FC<Props> = ({ items }) => {
	// Словари
	const {
		countries: allCountries,
		// cities: allCities,
		userTypes: allUserTypes,
		roles: allRoles,
	} = useAppSelector(LibrariesSelector);

	const [tableParams, setTableParams] = useState<TableParams>({
		pagination: {
			current: 1,
			pageSize: 10,
			total: 0,
		},
	});

	useEffect(
		() =>
			setTableParams({
				...tableParams,
				pagination: {
					...tableParams.pagination,
					total: items.length,
				},
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[items],
	);

	// Конфиг таблички для Ant Table
	const columns: ColumnsType<IUser> = [
		{
			title: '',
			dataIndex: 'avatar',
			key: 'avatar',
			width: 65,
			fixed: 'left',
			render: (avatar: IAvatar | null, user: IUser) => {
				const count = user?.logs?.filter((log: ILog) =>
					[3, 6, 7, 5].includes(log.logTypeId),
				).length;

				return count ? (
					<Badge count={count}>
						<Space size={64} wrap>
							{avatar ? (
								<Avatar src={<Image src={avatar.url} width={64} />} />
							) : (
								<Avatar icon={<UserOutlined />} />
							)}
						</Space>
					</Badge>
				) : (
					<Space size={64} wrap>
						{avatar ? (
							<Avatar src={<Image src={avatar.url} width={64} />} />
						) : (
							<Avatar icon={<UserOutlined />} />
						)}
					</Space>
				);
			},
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			ellipsis: true,
			width: 200,
			fixed: 'left',
			sorter: {
				compare: (a: IUser, b: IUser) => {
					if (a.name < b.name) return -1;
					if (a.name > b.name) return 1;
					return 0;
				},
				multiple: 1,
			},
			render: (name: string, user: IUser) => (
				<Flex vertical>
					<Text>{name}</Text>
					<Text type="secondary">{`id: ${user.id}`}</Text>
				</Flex>
			),
		},
		{
			title: 'Phone',
			dataIndex: 'phone',
			key: 'phone',
			ellipsis: true,
			width: 140,
			fixed: 'left',
			sorter: {
				compare: (a: IUser, b: IUser) => {
					if (a.phone < b.phone) return -1;
					if (a.phone > b.phone) return 1;
					return 0;
				},
				multiple: 1,
			},
			render: (phone: number | null, user: IUser) =>
				`${user.countryId ? allCountries.find((c: ICountry) => c.id === user.countryId).phoneCode : ''}${phone}`,
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			ellipsis: true,
			width: 200,
			sorter: {
				compare: (a: IUser, b: IUser) => {
					if (a.email < b.email) return -1;
					if (a.email > b.email) return 1;
					return 0;
				},
				multiple: 1,
			},
		},
		// {
		//   title: 'UID',
		//   dataIndex: 'uid',
		//   key: 'uid',
		//   sortOrder: sort.columnKey === 'uid' && sort.order,
		//   ellipsis: true,
		//   sorter: {
		//     compare: (a: IUserData, b: IUserData) => {
		//       if (a.uid < b.uid) return -1;
		//       if (a.uid > b.uid) return 1;
		//       return 0;
		//     },
		//     multiple: 1,
		//   },
		// },
		{
			title: 'Country',
			dataIndex: 'countryId',
			key: 'countryId',
			ellipsis: true,
			width: 170,
			sorter: (a: IUser, b: IUser) => {
				const contryName1 =
					allCountries?.find((item: ICountry) => item.id === a.countryId)?.name_en || '';
				const contryName2 =
					allCountries?.find((item: ICountry) => item.id === b.countryId)?.name_en || '';

				if (contryName1 < contryName2) {
					return -1;
				}
				if (contryName1 > contryName2) {
					return 1;
				}
				return 0;
			},
			filters: [
				...[{ text: '-', value: null }],
				...sortBy(allCountries, 'name').map((item: ICountry) => ({
					text: item.name_en,
					value: item.id,
				})),
			],
			onFilter: (value: number, user: IUser) => user.countryId === value,
			render: (countryId: number | null) =>
				allCountries?.find((item: ICountry) => item.id === countryId)?.name_en || '-',
		},
		{
			title: 'Type',
			dataIndex: 'userTypeId',
			key: 'userTypeId',
			width: 150,
			render: (userTypeId: number) => (
				<Tag bordered={false} color={userTypeId === 1 ? 'magenta' : 'blue'}>
					{allUserTypes?.find((item: IUserType) => item.id === userTypeId)?.name}
				</Tag>
			),
		},
		{
			title: 'Role',
			dataIndex: 'roleId',
			key: 'roleId',
			width: 150,
			render: (roleId: number) => (
				<Tag bordered={false} color={roleId === 1 ? 'default' : 'gold'}>
					{allRoles?.find((item: IRole) => item.id === roleId)?.name}
				</Tag>
			),
		},
		{
			title: 'Onboarding',
			dataIndex: 'onboarding',
			key: 'onboarding',
			width: 150,
			sorter: {
				compare: (a: IUser, b: IUser) => {
					if (a.onboarding < b.onboarding) return -1;
					if (a.onboarding > b.onboarding) return 1;
					return 0;
				},
				multiple: 1,
			},
			filters: [
				{
					text: 'viewed',
					value: false,
				},
				{
					text: 'new',
					value: true,
				},
			],
			onFilter: (value: boolean, user: IUser) => user.onboarding === value,
			render: (onboarding: boolean) => (
				<Tag bordered={false} color={onboarding ? 'cyan' : 'default'}>
					{onboarding ? 'new' : 'viewed'}
				</Tag>
			),
		},
		{
			title: 'Accepted',
			dataIndex: 'accepted',
			key: 'accepted',
			width: 150,
			sorter: {
				compare: (a: IUser, b: IUser) => {
					if (a.accepted < b.accepted) return -1;
					if (a.accepted > b.accepted) return 1;
					return 0;
				},
				multiple: 1,
			},
			filters: [
				{
					text: 'no',
					value: false,
				},
				{
					text: 'yes',
					value: true,
				},
			],
			onFilter: (value: boolean, user: IUser) => user.accepted === value,
			render: (accepted: boolean) => (
				<Tag bordered={false} color={accepted ? 'lime' : 'default'}>
					{accepted ? 'yes' : 'no'}
				</Tag>
			),
		},
		{
			title: 'Favourites',
			dataIndex: 'favourites',
			key: 'favourites',
			width: 100,
			sorter: {
				compare: (a: IUser, b: IUser) => {
					if (a?.favourites?.length < b?.favourites?.length) return -1;
					if (a.favourites?.length > b.favourites?.length) return 1;
					return 0;
				},
				multiple: 1,
			},
			render: (favourites: IFavourite[]) => favourites.length,
		},
		{
			title: 'Counsultatios',
			dataIndex: 'consultations',
			key: 'consultations',
			width: 150,
			sorter: {
				compare: (a: IUser, b: IUser) => {
					if (a?.consultations?.length < b?.consultations?.length) return -1;
					if (a.consultations?.length > b.consultations?.length) return 1;
					return 0;
				},
				multiple: 1,
			},
			render: (consultations: IConsultation[]) => consultations.length,
		},
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			ellipsis: true,
			sorter: (a: IUser, b: IUser) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
			// defaultSortOrder: 'descend',
			width: 170,
			fixed: 'right',
			render: (createdAt: string) => dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss'),
		},
		{
			title: 'Activity',
			dataIndex: 'logs',
			key: 'logs',
			width: 250,
			fixed: 'right',
			sorter: (a: IUser, b: IUser) => {
				const logsA = sortBy(a?.logs || [], 'createdAt');
				const logsB = sortBy(b?.logs || [], 'createdAt');
				const lastLogA = logsA?.[logsA.length - 1] || null;
				const lastLogB = logsB?.[logsB.length - 1] || null;

				return dayjs(lastLogA?.createdAt).valueOf() - dayjs(lastLogB?.createdAt).valueOf();
			},
			defaultSortOrder: 'descend',
			render: (logs: ILog[]) => {
				const days = uniq(logs.map((log: ILog) => dayjs(log.createdAt).format('YYYY.MM.DD')));

				if (!logs.length)
					return (
						<Tag color="red" bordered={false}>
							absent
						</Tag>
					);

				return (
					<Flex
						style={{
							fontSize: 16,
							color: '#1677ff',
						}}
						gap={16}>
						<Popover
							placement="left"
							overlayStyle={{
								width: 1000,
								padding: 0,
							}}
							overlayInnerStyle={{
								maxHeight: 600,
								overflowY: 'scroll',
							}}
							content={() => <Logs items={logs} />}>
							<LineChartOutlined />
						</Popover>
						<Flex vertical>
							<Text>{`${logs.length} (${days.length} days)`}</Text>
							<Text type="secondary">
								{sortBy(logs, 'createdAt')?.[logs.length - 1]
									? `last: ${dayjs(sortBy(logs, 'createdAt')?.[logs.length - 1]?.createdAt).format(
											'DD.MM.YYYY HH:mm:ss',
										)}`
									: '-'}
							</Text>
						</Flex>
					</Flex>
				);
			},
		},
	];

	const onHandleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue>,
		sorter: SorterResult<IUser>,
	) => {
		setTableParams({
			pagination,
			filters,
			...sorter,
		});
	};

	return (
		<S.Wrap>
			<TableAnt
				columns={columns}
				rowKey={(lot: IUser) => lot.id}
				dataSource={items}
				scroll={{ x: '1000' }}
				pagination={false}
				onChange={onHandleTableChange}
			/>
		</S.Wrap>
	);
};

export default Table;
